// Client helpers for server-side report export. Generation (CSV / PDF) now
// happens on the server — the client only describes the columns it currently
// shows and triggers the download of the bytes the server returns. This keeps
// large exports off the browser: the server queries every matching row in one
// pass instead of the client paging through them.
import type { ColumnConfig } from '@/components/shared/report-controls';

export type ExportFormat = 'text' | 'date' | 'datetime' | 'number' | 'boolean' | 'image';

/** Downloadable export file formats offered in the Export dropdown. */
export type ExportFileFormat = 'csv' | 'json' | 'xml' | 'html' | 'pdf';

export const EXPORT_FILE_FORMATS: Array<{ value: ExportFileFormat; label: string }> = [
  { value: 'csv', label: 'CSV' },
  { value: 'json', label: 'JSON' },
  { value: 'xml', label: 'XML' },
  { value: 'html', label: 'HTML' },
  { value: 'pdf', label: 'PDF' },
];

export interface ExportColumnSpec {
  key: string;
  label: string;
  format: ExportFormat;
}

/** Best-effort format for a column when it doesn't declare `exportFormat`. */
function inferFormat(col: ColumnConfig): ExportFormat {
  if (col.exportFormat) return col.exportFormat;
  switch (col.filterType) {
    case 'number': return 'number';
    case 'boolean': return 'boolean';
    case 'date': return 'date';
    default: return 'text';
  }
}

/** Formats that render the image itself — PDF embeds a thumbnail, HTML renders
 *  an <img>. Text-only formats (CSV/JSON/XML) drop image columns entirely so
 *  the signed S3 URLs are never exposed. */
const IMAGE_CAPABLE_FORMATS: ExportFileFormat[] = ['pdf', 'html'];

/** Map the currently-visible columns to the spec the export endpoint expects. */
export function buildExportColumns(
  columns: ColumnConfig[],
  format: ExportFileFormat,
): ExportColumnSpec[] {
  const keepImages = IMAGE_CAPABLE_FORMATS.includes(format);
  return columns
    .map((c) => ({ key: c.key, label: c.label, format: inferFormat(c) }))
    .filter((c) => c.format !== 'image' || keepImages);
}

/** Filesystem-safe slug for a generated download filename. */
export function fileSlug(s: string): string {
  return s.replace(/[^a-z0-9-_]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'report';
}

/**
 * Load a PDF blob into a hidden iframe and fire the browser's print dialog on
 * it. Avoids the popup blocker (no `window.open`) and prints directly instead
 * of leaving the user on a PDF-viewer tab. Falls back to opening the PDF in a
 * new tab if the iframe can't be printed (e.g. Safari). The iframe + object
 * URL are cleaned up after a delay so the print job isn't cut off.
 */
export function printPdfBlob(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.src = url;
  iframe.onload = () => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch {
      window.open(url, '_blank');
    }
  };
  document.body.appendChild(iframe);
  setTimeout(() => {
    iframe.remove();
    URL.revokeObjectURL(url);
  }, 60_000);
}

/** Save a Blob to disk under `filename`. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
